import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useMutation } from '@tanstack/vue-query'
import { baseApiClient, feedbackApiClient } from '@/api/clients'
import { AxiosError, AxiosResponse } from 'axios'
import { BaseApiSendMessageResponseFeedbackVars, BaseApiSendMessageVars } from '@/types/api'
import { v4 as uuidv4 } from 'uuid'
import { useCustomParams } from '@/composables/useCustomParams'

export const useCustomGPT = defineStore('customGPT', () => {
  const sessionId = ref<string | null>(localStorage.getItem('sessionId'))
  const { email, user_id, source, language } = useCustomParams()

  const { mutateAsync: createConversation } = useMutation<AxiosResponse, AxiosError>({
    mutationKey: ['createConversation'],
    mutationFn: () => {
      const guid = uuidv4()
      const url = null

      const conversationNameData = {
        guid,
        user_id,
        email,
        source,
        url
      }

      return baseApiClient.post('/conversations', {
        name: JSON.stringify(conversationNameData)
      })
    },
    onError: err => {
      console.log(err)
    },
    onSuccess: resp => {
      localStorage.setItem('sessionId', resp.data.data.session_id)
      sessionId.value = resp.data.data.session_id
    }
  })

  const { mutateAsync: sendMessage } = useMutation<
    AxiosResponse<ReadableStream<Uint8Array>>,
    AxiosError,
    BaseApiSendMessageVars
  >({
    mutationKey: ['sendMessage'],
    mutationFn: ({ message }) => {
      const stream = true

      return baseApiClient.post(
        `/conversations/${sessionId.value}/messages?stream=${stream}&lang=${language}`,
        {
          prompt: message,
          response_source: 'default'
        },
        {
          responseType: 'stream',
          adapter: 'fetch',
          validateStatus: status => status < 500
        }
      )
    }
  })

  const { mutateAsync: sendMessageResponseFeedback } = useMutation<
    unknown,
    AxiosError,
    BaseApiSendMessageResponseFeedbackVars
  >({
    mutationKey: ['sendMessageResponseFeedback'],
    mutationFn: ({ promptId, reaction }) =>
      feedbackApiClient.post(`/prompts/${promptId}/message-response-feedback`, {
        reaction: reaction
      }),
    onError: err => {
      console.log(err)
    }
  })

  const sendMessageToConversation = async (message: string): Promise<ReadableStream<Uint8Array>> => {
    if (sessionId.value === null) {
      await createConversation()
    }

    const resp = await sendMessage({ message })

    if (resp.status !== 200) {
      const x = resp.data.pipeThrough(new TextDecoderStream()).getReader()
      const y = await x.read()
      const { message } = JSON.parse(y.value!)

      const stream = await getReadableStreamForMessage(message)
      return stream
    }

    return resp.data
  }

  const getReadableStreamForMessage = async (msg: string): Promise<ReadableStream<Uint8Array>> => {
    const encoder = new TextEncoder()
    const events = [
      {
        event: 'start',
        data: { status: 'start', prompt: '' }
      },
      {
        event: 'progress',
        data: { status: 'progress', message: msg }
      },
      {
        event: 'finish',
        data: {
          status: 'finish',
          created_at: new Date().toISOString(),
          citations: null
        }
      }
    ]

    const stream = new ReadableStream({
      start(controller) {
        for (const { event, data } of events) {
          const message = `event: ${event}\ndata: ${JSON.stringify(data)}\n\n`
          controller.enqueue(encoder.encode(message))
        }
        controller.close()
      }
    })

    return stream
  }

  return {
    createConversation,
    sendMessageToConversation,
    sendMessageResponseFeedback,
    getReadableStreamForMessage
  }
})
